/**
 * Column definitions for the results table.
 *
 * One typed list drives the header, the body and the mobile card list, so the
 * three cannot drift apart.
 *
 * Widths are explicit and the table is laid out `fixed`: with nine columns of
 * free-flowing text the browser's automatic algorithm pushed the last two off
 * the viewport entirely. Fixed widths keep every column on screen and give the
 * two free-text columns the leftover space to share.
 */
import type { ReactNode } from 'react'

import { StatusDot } from '@/components/judgments/StatusBadge'
import { EMPTY, formatMoney, formatNames } from '@/lib/format'
import type { Judgment, SortField } from '@/types/api'

export interface JudgmentColumn {
  id: string
  header: string
  /** Set when the API can sort by this column. */
  sortField?: SortField
  align?: 'left' | 'right'
  /** Column width. `null` means "share the remaining space". */
  width: string | null
  /** Tailwind classes hiding the column below a breakpoint. */
  responsiveClass?: string
  cellClass?: string
  cell: (judgment: Judgment) => ReactNode
  /** Plain text for the cell's title tooltip. */
  title?: (judgment: Judgment) => string
}

/**
 * One placeholder for an absent value, matching `format.EMPTY`.
 *
 * `muted`, not `faint`: "this judgment has no docket number" is information,
 * and faint is reserved for marks nobody has to read.
 */
const Blank = () => <span className="text-ink-muted">{EMPTY}</span>

export const JUDGMENT_COLUMNS: JudgmentColumn[] = [
  {
    id: 'judgmentNumber',
    header: 'Judgment #',
    sortField: 'judgment_number',
    width: '8.75rem',
    cellClass: 'ident font-medium text-ink',
    cell: (j) => j.judgmentNumber || <Blank />,
  },
  {
    id: 'dateEntered',
    header: 'Entered',
    sortField: 'entered_on',
    width: '6.25rem',
    cellClass: 'ident text-ink-secondary',
    cell: (j) => j.dateEntered || <Blank />,
  },
  {
    id: 'debtors',
    header: 'Debtor',
    width: null,
    cellClass: 'text-ink',
    cell: (j) => <span className="block truncate">{formatNames(j.debtorNames, 1)}</span>,
    title: (j) => j.debtorNames.join('; '),
  },
  {
    id: 'creditors',
    header: 'Creditor',
    width: null,
    responsiveClass: 'hidden lg:table-cell',
    cellClass: 'text-ink-secondary',
    cell: (j) => <span className="block truncate">{formatNames(j.creditorNames, 1)}</span>,
    title: (j) => j.creditorNames.join('; '),
  },
  {
    id: 'amount',
    header: 'Amount',
    sortField: 'amount_value',
    align: 'right',
    width: '7rem',
    // The decision field: is this lien worth acting on. Mono + medium weight.
    cellClass: 'ident text-right font-medium text-ink',
    cell: (j) => (j.amountValue ? formatMoney(j.amountValue) : <Blank />),
  },
  {
    id: 'status',
    header: 'Status',
    sortField: 'status',
    width: '5.75rem',
    cell: (j) => <StatusDot status={j.status} statusText={j.statusText} />,
  },
  {
    id: 'filingLocation',
    header: 'County',
    sortField: 'filing_location',
    width: '6.5rem',
    responsiveClass: 'hidden md:table-cell',
    cellClass: 'truncate text-ink-secondary',
    cell: (j) => (j.filingLocation ? titleCaseCounty(j.filingLocation) : <Blank />),
  },
  {
    id: 'docketNumber',
    header: 'Docket #',
    sortField: 'docket_number',
    width: '8rem',
    responsiveClass: 'hidden xl:table-cell',
    cellClass: 'ident text-ink-secondary',
    cell: (j) => j.docketNumber || <Blank />,
  },
]

function titleCaseCounty(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase()
}
